import { usePersistedState } from "@/lib/persistedFilters";
// Emergency Numbers → Numbers. One row per emergency number the Portal knows,
// matched against what Bandwidth holds for it.
import { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Download, Loader2, Play, Search } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Empty, ModePill, NUMBER_STATUS, NUMBER_STATUS_ORDER, PLATFORM_LABEL, Pager, Pill, SIDE, fmtTn } from "./ui";

const LIMIT = 100;
const TYPES = [["", "Any side"], ["legacy", "Portal: Legacy"], ["dlr", "Portal: Endpoint"], ["user_only", "Portal: user only"],
  ["static", "Bandwidth: static"], ["none", "Nowhere"]];

export function NumberStatusPill({ status, testid }) {
  const s = NUMBER_STATUS[status] || { label: status, tone: "bg-slate-100 text-slate-600" };
  return <Pill tone={s.tone} title={s.hint} testid={testid}>{s.label}</Pill>;
}

export default function Numbers({ overview, canEdit, filter, refreshKey, onOpenDomain, onStarted }) {
  const [status, setStatus] = usePersistedState("e911-numbers.status", "", filter?.status);
  const [type, setType] = usePersistedState("e911-numbers.type", "");
  const [territory, setTerritory] = usePersistedState("e911-numbers.territory", "");
  const [platform, setPlatform] = usePersistedState("e911-numbers.platform", "");
  const [q, setQ] = usePersistedState("e911-numbers.q", "");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const platforms = overview?.settings?.platforms || ["core"];
  const built = overview?.settings?.numbers_built_at;
  const bwSeen = overview?.settings?.bandwidth_seen && Object.values(overview.settings.bandwidth_seen).some(Boolean);

  const lastFilter = useRef(filter);
  // A filter handed down from the Overview wins — but not on mount, where the remembered status is kept.
  useEffect(() => {
    if (lastFilter.current === filter) return;
    lastFilter.current = filter;
    setStatus(filter?.status || ""); setPage(1);
  }, [filter, setStatus]);

  const params = { status, type, territory, platform, q };
  const load = useCallback(async () => {
    setBusy("load");
    try {
      const { data: d } = await api.get("/e911/numbers", { params: { ...params, page, limit: LIMIT } });
      setData(d);
    } catch {
      toast.error("Could not load the numbers report");
    } finally {
      setBusy("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, type, territory, platform, q, page]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const exportCsv = async () => {
    setBusy("csv");
    try {
      const res = await api.get("/e911/numbers.csv", { params, responseType: "blob" });
      downloadBlob(res.data, `emergency-numbers-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      toast.error("Export failed");
    } finally {
      setBusy("");
    }
  };

  const build = async () => {
    setBusy("build");
    try {
      await api.post("/e911/scan", { scope: "all", build_numbers: true });
      toast.success("Full scan started — the report fills in when it finishes (see Runs)");
      onStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the scan");
    } finally {
      setBusy("");
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const pick = (setter) => (v) => { setter(v); setPage(1); };
  const counts = data?.counts || {};

  return (
    <div className="space-y-4" data-testid="e911-numbers">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={status === "" ? "default" : "outline"} onClick={() => pick(setStatus)("")}
                data-testid="e911-numbers-status-all">All</Button>
        {NUMBER_STATUS_ORDER.map((k) => (
          <Button key={k} size="sm" variant={status === k ? "default" : "outline"} onClick={() => pick(setStatus)(k)}
                  data-testid={`e911-numbers-status-${k}`} title={NUMBER_STATUS[k].hint}>
            {NUMBER_STATUS[k].label}{counts[k] ? ` (${counts[k].toLocaleString()})` : ""}
          </Button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-200" />
        <select value={type} onChange={(e) => pick(setType)(e.target.value)} data-testid="e911-numbers-type"
                className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
          {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={territory} onChange={(e) => pick(setTerritory)(e.target.value)} data-testid="e911-numbers-territory"
                className="h-8 max-w-[14rem] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
          <option value="">All territories</option>
          {(data?.territories || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {platforms.length > 1 && (
          <select value={platform} onChange={(e) => pick(setPlatform)(e.target.value)} data-testid="e911-numbers-platform"
                  className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
            <option value="">All platforms</option>
            {platforms.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p] || p}</option>)}
          </select>
        )}
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-64 pl-8" placeholder="Number, domain, user or name" value={q} data-testid="e911-numbers-search"
                 onChange={(e) => pick(setQ)(e.target.value)} />
        </div>
        {busy === "load" && <Loader2 className="h-4 w-4 animate-spin text-slate-300" />}
        <span className="text-xs text-slate-500" data-testid="e911-numbers-total">{(data?.total || 0).toLocaleString()} numbers</span>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={!!busy || !data?.total} data-testid="e911-numbers-export">
          {busy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
        </Button>
      </div>

      {status && <p className="text-[12px] text-slate-500" data-testid="e911-numbers-hint">{NUMBER_STATUS[status]?.hint}</p>}
      {built && (
        <p className="text-[11px] text-slate-400" data-testid="e911-numbers-snapshot-hint">
          Snapshot built {Object.entries(built).map(([p, at]) => `${PLATFORM_LABEL[p] || p} ${new Date(at).toLocaleString()}`).join(", ")}.
          Rows refresh for a domain when you open it and <strong>Re-assess live</strong> or apply a fix; a scan rebuilds everything.
        </p>
      )}

      {built && !bwSeen && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="e911-numbers-no-bandwidth">
          This report was built while Bandwidth was not connected, so <strong>every number reads as not registered</strong>.
          Connect Bandwidth under Settings → API Connections and rebuild it.
        </div>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Number</th>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Territory</th>
              <th className="px-3 py-1.5">Mode</th>
              <th className="px-3 py-1.5">Portal side</th>
              <th className="px-3 py-1.5">Bandwidth side</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5">Users</th>
              <th className="px-3 py-1.5">Caller name · address</th>
            </tr>
          </thead>
          <tbody data-testid="e911-numbers-body">
            {(data?.items || []).map((r) => (
              <tr key={`${r.platform}/${r.domain}/${r.number}`} className="border-t border-slate-100" data-testid={`e911-number-row-${r.number}`}>
                <td className="px-3 py-1.5 font-mono text-[12px] text-slate-800">{fmtTn(r.number)}</td>
                <td className="px-3 py-1.5">
                  {r.domain ? (
                    <button type="button" className="font-medium text-slate-800 hover:text-primary hover:underline"
                            onClick={() => onOpenDomain({ platform: r.platform, domain: r.domain })}
                            data-testid={`e911-number-domain-${r.number}`}>{r.domain}</button>
                  ) : <span className="text-slate-400">no Portal domain</span>}
                </td>
                <td className="px-3 py-1.5 text-slate-500">{r.territory || "—"}</td>
                <td className="px-3 py-1.5">{r.expected_mode ? <ModePill mode={r.expected_mode} /> : <span className="text-slate-300">—</span>}</td>
                <td className="px-3 py-1.5 text-slate-700">{SIDE[r.portal_type] || r.portal_type}</td>
                <td className="px-3 py-1.5 text-slate-700">
                  {SIDE[r.bandwidth_type] || r.bandwidth_type}
                  {r.bw_status && r.bw_status !== "PROVISIONED" && <span className="ml-1 text-[11px] text-amber-700">({r.bw_status})</span>}
                </td>
                <td className="px-3 py-1.5"><NumberStatusPill status={r.status} testid={`e911-number-status-${r.number}`} /></td>
                <td className="px-3 py-1.5 text-slate-600" title={(r.users || []).join(", ")}>
                  {r.user_count || 0}{r.sites?.length ? <span className="text-[11px] text-slate-400"> · {r.sites.join(", ")}</span> : ""}
                </td>
                <td className="px-3 py-1.5 text-[12px] text-slate-600">
                  {r.caller_name && <div className="text-slate-800">{r.caller_name}</div>}
                  <div className="text-slate-500">{r.address || "—"}</div>
                </td>
              </tr>
            ))}
            {data && !data.items.length && (
              <Empty colSpan={9} testid="e911-numbers-empty">
                {!built ? (
                  <div className="space-y-2">
                    <div>The numbers report has not been built yet. The nightly sweep builds it at 04:30, or start one now.</div>
                    {canEdit && (
                      <Button size="sm" onClick={build} disabled={!!busy || !!overview?.running} data-testid="e911-numbers-build">
                        {busy === "build" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                        {overview?.running ? "A run is in progress" : "Scan every domain and build the report"}
                      </Button>
                    )}
                  </div>
                ) : "No number matches those filters."}
              </Empty>
            )}
          </tbody>
        </table>
      </Card>
      <Pager page={page} pages={pages} onPage={setPage} testid="e911-numbers" />
    </div>
  );
}
